import styled from "styled-components";

import { Avatar } from "MVVM/Components/AvatarV2";
import { TUserGroup } from "MVVM/@types";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { ICompetitionFeedClip } from "../../../Competitions/@types";

interface CompactClipCardProps {
  clip: ICompetitionFeedClip;
  submittedAt?: string;
  onClick: () => void;
}

export const CompactClipCard = ({
  clip,
  submittedAt,
  onClick,
}: CompactClipCardProps) => {
  const date = submittedAt ?? clip.createdAt;
  return (
    <Card
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      role="button"
      tabIndex={0}
    >
      <Thumb src={clip.thumbnailUrl || undefined} />
      <MetaRow>
        <Avatar
          src={clip.user?.avatarUrl || undefined}
          size="sm"
          userGroup={clip.user?.userGroup?.type as TUserGroup}
          profileCardUsername={clip.user?.username ?? undefined}
        />
        <MetaInfo>
          <Title>{clip.title || "Untitled Clip"}</Title>
          <Username>{clip.user?.username ?? "Unknown"}</Username>
          {date && <Time>{dateFormatWithDistanceSuffix(date)}</Time>}
        </MetaInfo>
      </MetaRow>
    </Card>
  );
};

const Card = styled.div`
  flex: 0 0 196px;
  width: 196px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;

  &:hover > div:first-child {
    opacity: 1;
  }
`;

const Thumb = styled.div<{ src?: string }>`
  aspect-ratio: 16 / 9;
  width: 100%;
  border-radius: 10px;
  background: ${({ src, theme }) =>
    src ? `url(${src}) center / cover` : theme.colors.navy500};
  box-shadow: 0 0 0 1px rgba(143, 169, 175, 0.12);
  opacity: 0.82;
  transition: opacity 0.15s ease;
`;

const Title = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  & > :first-child {
    flex-shrink: 0;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
`;

const Username = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.chalk};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
`;

const Time = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.chalk};
  white-space: nowrap;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
`;
